import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Radio, Video, VideoOff, Mic, MicOff, Settings,
  Play, Square, Users, Eye, Heart, MessageCircle,
  Send, X, Maximize, Minimize, RotateCcw, Flame,
  AlertCircle, CheckCircle, Camera, Monitor
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BroadcastPage() {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  // Stream state
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [streamDuration, setStreamDuration] = useState(0);
  
  // Media state
  const [hasPermissions, setHasPermissions] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoDevices, setVideoDevices] = useState([]);
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState('');
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('');
  const [mediaError, setMediaError] = useState(null);
  
  // Stats
  const [viewerCount, setViewerCount] = useState(0);
  const [likesCount, setLikesCount] = useState(0);
  const [barkaReceived, setBarkaReceived] = useState(0);
  
  // Chat
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showChat, setShowChat] = useState(true);
  
  // Refs
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const pollingRef = useRef(null);
  
  // Get available devices
  const getDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter(d => d.kind === 'videoinput');
      const audioInputs = devices.filter(d => d.kind === 'audioinput');
      
      setVideoDevices(videoInputs);
      setAudioDevices(audioInputs);
      
      if (videoInputs.length > 0 && !selectedVideoDevice) {
        setSelectedVideoDevice(videoInputs[0].deviceId);
      }
      if (audioInputs.length > 0 && !selectedAudioDevice) {
        setSelectedAudioDevice(audioInputs[0].deviceId);
      }
    } catch (err) {
      console.error('Failed to enumerate devices:', err);
    }
  };
  
  // Request media permissions and start preview
  const startPreview = async () => {
    try {
      setMediaError(null);
      
      const constraints = {
        video: selectedVideoDevice 
          ? { deviceId: { exact: selectedVideoDevice }, width: 1280, height: 720 }
          : { width: 1280, height: 720 },
        audio: selectedAudioDevice 
          ? { deviceId: { exact: selectedAudioDevice } }
          : true
      };
      
      // Stop previous stream if exists
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = mediaStream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      
      setHasPermissions(true);
      await getDevices();
      
    } catch (err) {
      console.error('Failed to get media:', err);
      setMediaError(err.message || 'Impossible d\'accéder à la caméra/micro');
      setHasPermissions(false);
    }
  };
  
  // Toggle video
  const toggleVideo = () => {
    if (streamRef.current) {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoEnabled(videoTrack.enabled);
      }
    }
  };
  
  // Toggle audio
  const toggleAudio = () => {
    if (streamRef.current) {
      const audioTrack = streamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
      }
    }
  };
  
  // Change video device
  const changeVideoDevice = async (deviceId) => {
    setSelectedVideoDevice(deviceId);
    if (hasPermissions) {
      await startPreview();
    }
  };
  
  // Change audio device
  const changeAudioDevice = async (deviceId) => {
    setSelectedAudioDevice(deviceId);
    if (hasPermissions) {
      await startPreview();
    }
  };
  
  // Fetch stream info
  const fetchStream = useCallback(async () => {
    if (!streamId || !token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/streams/my-stream/${streamId}/broadcast`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setStream(data);
        setIsLive(data.status === 'live');
        setViewerCount(data.viewer_count || 0);
        setLikesCount(data.likes_count || 0);
        setBarkaReceived(data.barka_donated || 0);
      } else {
        navigate('/live');
      }
    } catch (err) {
      console.error('Failed to fetch stream:', err);
    } finally {
      setLoading(false);
    }
  }, [streamId, token, navigate]);
  
  // Fetch chat messages
  const fetchMessages = useCallback(async () => {
    if (!streamId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/chat`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  }, [streamId]);
  
  // Start the live stream
  const startStream = async () => {
    if (!stream || !hasPermissions) return;
    
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setIsLive(true);
        setStreamDuration(0);
        
        // Start duration counter
        durationIntervalRef.current = setInterval(() => {
          setStreamDuration(prev => prev + 1);
        }, 1000);
        
        fetchStream();
      } else {
        const data = await res.json();
        alert(data.detail || 'Erreur lors du démarrage');
      }
    } catch (err) {
      console.error('Failed to start stream:', err);
    }
  };
  
  // End the live stream
  const endStream = async () => {
    if (!stream) return;
    
    if (!window.confirm('Voulez-vous vraiment terminer le live ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setIsLive(false);
        
        if (durationIntervalRef.current) {
          clearInterval(durationIntervalRef.current);
        }
        
        // Stop media
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
        
        navigate('/live');
      }
    } catch (err) {
      console.error('Failed to end stream:', err);
    }
  };
  
  // Send chat message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !token) return;
    
    try {
      await fetch(`${API_URL}/api/streams/${streamId}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: newMessage })
      });
      
      setNewMessage('');
      fetchMessages();
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };
  
  // Format duration
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Initial load
  useEffect(() => {
    fetchStream();
    startPreview();
    
    return () => {
      // Cleanup
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [fetchStream]);
  
  // Polling when live
  useEffect(() => {
    if (isLive) {
      pollingRef.current = setInterval(() => {
        fetchStream();
        fetchMessages();
      }, 3000);
      
      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
        }
      };
    }
  }, [isLive, fetchStream, fetchMessages]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-950">
        <div className="animate-spin h-12 w-12 border-4 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  return (
    <div className="h-screen bg-gray-950 flex" data-testid="broadcast-page">
      {/* Main Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-gray-900 border-b border-gray-800 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/live')}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </Button>
              
              <div>
                <h1 className="text-lg font-semibold text-white flex items-center gap-2">
                  {isLive && (
                    <Badge className="bg-red-500 text-white animate-pulse">
                      <div className="w-2 h-2 bg-white rounded-full mr-1" />
                      LIVE
                    </Badge>
                  )}
                  {stream?.title || 'Mon Live'}
                </h1>
                {isLive && (
                  <p className="text-sm text-gray-400">
                    Durée: {formatDuration(streamDuration)}
                  </p>
                )}
              </div>
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 text-gray-400">
                <Eye className="h-4 w-4" />
                <span className="font-medium text-white">{viewerCount}</span>
                <span className="text-sm">spectateurs</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Heart className="h-4 w-4 text-red-500" />
                <span className="font-medium text-white">{likesCount}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Flame className="h-4 w-4 text-orange-500" />
                <span className="font-medium text-white">{barkaReceived}</span>
                <span className="text-sm">BARKA</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Video Preview */}
        <div className="flex-1 relative bg-black flex items-center justify-center">
          {hasPermissions ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className={`w-full h-full object-cover ${!videoEnabled ? 'hidden' : ''}`}
              />
              
              {!videoEnabled && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                  <div className="text-center">
                    <VideoOff className="h-16 w-16 mx-auto text-gray-600 mb-4" />
                    <p className="text-gray-400">Caméra désactivée</p>
                  </div>
                </div>
              )}
              
              {/* Live indicator overlay */}
              {isLive && (
                <div className="absolute top-4 left-4">
                  <Badge className="bg-red-500 text-white text-lg px-4 py-2">
                    <div className="w-3 h-3 bg-white rounded-full mr-2 animate-pulse" />
                    EN DIRECT
                  </Badge>
                </div>
              )}
              
              {/* Viewer count overlay */}
              {isLive && (
                <div className="absolute top-4 right-4">
                  <Badge variant="outline" className="bg-black/50 text-white border-0 text-lg px-4 py-2">
                    <Eye className="h-4 w-4 mr-2" />
                    {viewerCount}
                  </Badge>
                </div>
              )}
            </>
          ) : (
            <div className="text-center p-8">
              {mediaError ? (
                <>
                  <AlertCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
                  <h2 className="text-xl font-semibold text-white mb-2">Erreur d'accès</h2>
                  <p className="text-gray-400 mb-4">{mediaError}</p>
                  <Button onClick={startPreview} className="bg-orange-500 hover:bg-orange-600">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Réessayer
                  </Button>
                </>
              ) : (
                <>
                  <Camera className="h-16 w-16 mx-auto text-gray-600 mb-4" />
                  <h2 className="text-xl font-semibold text-white mb-2">Autorisation requise</h2>
                  <p className="text-gray-400 mb-4">
                    Autorisez l'accès à votre caméra et microphone pour démarrer le live
                  </p>
                  <Button onClick={startPreview} className="bg-orange-500 hover:bg-orange-600">
                    <Camera className="h-4 w-4 mr-2" />
                    Autoriser l'accès
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
        
        {/* Controls */}
        <div className="bg-gray-900 border-t border-gray-800 p-4">
          <div className="flex items-center justify-between">
            {/* Media controls */}
            <div className="flex items-center gap-3">
              <Button
                variant={videoEnabled ? 'default' : 'destructive'}
                size="lg"
                onClick={toggleVideo}
                disabled={!hasPermissions}
                className={videoEnabled ? 'bg-gray-700 hover:bg-gray-600' : ''}
              >
                {videoEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
              </Button>
              
              <Button
                variant={audioEnabled ? 'default' : 'destructive'}
                size="lg"
                onClick={toggleAudio}
                disabled={!hasPermissions}
                className={audioEnabled ? 'bg-gray-700 hover:bg-gray-600' : ''}
              >
                {audioEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
              </Button>
              
              {/* Device selectors */}
              {videoDevices.length > 1 && (
                <Select value={selectedVideoDevice} onValueChange={changeVideoDevice}>
                  <SelectTrigger className="w-[180px] bg-gray-800 border-gray-700 text-white">
                    <Camera className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Caméra" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {videoDevices.map(device => (
                      <SelectItem 
                        key={device.deviceId} 
                        value={device.deviceId}
                        className="text-white hover:bg-gray-700"
                      >
                        {device.label || `Caméra ${videoDevices.indexOf(device) + 1}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {audioDevices.length > 1 && (
                <Select value={selectedAudioDevice} onValueChange={changeAudioDevice}>
                  <SelectTrigger className="w-[180px] bg-gray-800 border-gray-700 text-white">
                    <Mic className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Micro" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {audioDevices.map(device => (
                      <SelectItem 
                        key={device.deviceId} 
                        value={device.deviceId}
                        className="text-white hover:bg-gray-700"
                      >
                        {device.label || `Micro ${audioDevices.indexOf(device) + 1}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* Start/Stop controls */}
            <div className="flex items-center gap-3">
              {!isLive ? (
                <Button
                  size="lg"
                  onClick={startStream}
                  disabled={!hasPermissions}
                  className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-8"
                  data-testid="start-stream-btn"
                >
                  <Play className="h-5 w-5 mr-2" />
                  Démarrer le live
                </Button>
              ) : (
                <Button
                  size="lg"
                  variant="destructive"
                  onClick={endStream}
                  className="px-8"
                  data-testid="stop-stream-btn"
                >
                  <Square className="h-5 w-5 mr-2" />
                  Terminer le live
                </Button>
              )}
              
              <Button
                variant="ghost"
                size="lg"
                onClick={() => setShowChat(!showChat)}
                className="text-gray-400 hover:text-white"
              >
                <MessageCircle className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Chat Panel */}
      {showChat && (
        <div className="w-80 bg-gray-900 border-l border-gray-800 flex flex-col">
          <div className="p-4 border-b border-gray-800">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-orange-500" />
              Chat en direct
            </h2>
          </div>
          
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-3">
              {messages.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  {isLive ? 'En attente de messages...' : 'Le chat sera actif pendant le live'}
                </p>
              ) : (
                messages.map((msg) => (
                  <div 
                    key={msg.id}
                    className={`${
                      msg.message_type === 'donation' 
                        ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 p-2 rounded-lg' 
                        : msg.message_type === 'system'
                        ? 'text-center'
                        : ''
                    }`}
                  >
                    {msg.message_type === 'system' ? (
                      <span className="text-xs text-gray-500">{msg.content}</span>
                    ) : (
                      <div className="flex items-start gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={msg.user?.profile_picture} />
                          <AvatarFallback className="text-xs bg-gray-700">
                            {msg.user?.display_name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-medium text-orange-400">
                            {msg.user?.display_name}
                          </span>
                          {msg.message_type === 'donation' && (
                            <span className="ml-1 text-xs text-yellow-400">
                              🔥 {msg.donation_amount}
                            </span>
                          )}
                          <p className="text-sm text-white break-words">{msg.content}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
          
          {isLive && (
            <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-800">
              <div className="flex gap-2">
                <Input
                  placeholder="Message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white text-sm"
                />
                <Button type="submit" size="icon" className="bg-orange-500">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
